/**
 * Confirming the e-mail address (S28, Martin, 25.10.2026): only a confirmed address gets a link for
 * a new password, and the link to confirm works only signed in as its account. Whoever got the mail
 * by a typo can confirm nothing.
 */
import { useEffect, useState } from "react";
import { NOT_CONFIRMED, SPAM_FOLDER, account } from "../account.ts";
import { ApiError, messageOf, store } from "../store.ts";
import type { User } from "../store.ts";

/** Under the address in „Konto“: whether it is confirmed, and „Mail noch einmal schicken“. */
export function EmailState({ user, onUser }: { user: User; onUser: (u: User) => void }) {
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  if (!user.email) return null;
  if (user.emailConfirmed && !user.emailPending) {
    return (
      <p className="ok-text small" data-testid="email-state">
        ✓ Bestätigt. An diese Adresse kommt der Link, wenn du dein Passwort vergisst.
      </p>
    );
  }
  async function send() {
    setState({ busy: true });
    try {
      const r = await account.sendConfirm();
      onUser(r.user);
      setState({ ok: `Die Mail ist unterwegs. Bitte öffne den Link darin, angemeldet mit diesem Konto. ${SPAM_FOLDER}` });
    } catch (err) {
      // Too many mails: some are on their way, so the spam folder is worth a look as well.
      setState({ error: err instanceof ApiError && err.code === "too_many" ? `${messageOf(err)} ${SPAM_FOLDER}` : messageOf(err) });
    }
  }
  return (
    <div className="email-state" data-testid="email-state">
      <p className="warn small">
        {user.emailPending
          ? `${NOT_CONFIRMED} Bis dahin gilt ${user.email}.`
          : `${NOT_CONFIRMED} Erst danach kommt hierher ein Link, wenn du dein Passwort vergisst.`}
      </p>
      <button className="link" disabled={state.busy} onClick={() => void send()} data-testid="email-send">
        Mail noch einmal schicken
      </button>
      {(state.ok || state.error) && <p className={state.error ? "error" : "ok-text"}>{state.error ?? state.ok}</p>}
    </div>
  );
}

/** Above the menus while the own address is not confirmed: one line, to „Konto“. */
export function EmailReminder() {
  const user = store.session?.user;
  if (store.session?.demo || !user?.email || user.emailConfirmed !== false) return null;
  return (
    <p className="banner email-reminder" data-testid="email-reminder">
      <span>Bitte bestätige deine E-Mail-Adresse, sonst kannst du ein vergessenes Passwort nicht selbst zurücksetzen.</span>
      <a href="#/konto">Zum Konto</a>
    </p>
  );
}

/** The page of the link in the mail (`#/email/<token>`), signed in. */
export function ConfirmEmail({ token }: { token: string }) {
  const [state, setState] = useState<{ done?: User; error?: string; other?: boolean } | null>(null);
  useEffect(() => {
    account
      .confirmEmail(token)
      .then((r) => [store.setUser(r.user), setState({ done: r.user })])
      .catch((err: unknown) => setState({ error: messageOf(err), other: err instanceof ApiError && err.code === "other_account" }));
  }, [token]);
  return (
    <div className="account">
      <section className="card" data-testid="email-confirm">
        <h2>E-Mail-Adresse bestätigen</h2>
        {!state ? (
          <p className="muted">Lädt …</p>
        ) : state.done ? (
          <p className="ok-text">Danke! {state.done.email} ist bestätigt. Vergisst du dein Passwort, kommt der Link dorthin.</p>
        ) : (
          <>
            <p className="error">{state.error}</p>
            {state.other && (
              <button onClick={() => void store.signOut()} data-testid="email-other-signout">
                Abmelden
              </button>
            )}
          </>
        )}
        <p>
          <a href="#/speisekarten">Weiter zu den Speisekarten</a>
        </p>
      </section>
    </div>
  );
}
